import { defineStore, storeToRefs } from 'pinia';
import { ref, computed } from 'vue';
import { queries } from '@/apollo';
import { Market, Query } from '@/generated/graphql.ts';
import { apolloService } from '@/apollo/apolloRequests.ts';
import { useStorage, StorageSerializers } from '@vueuse/core';

import { SimpleMarket } from './marketSelectorTypes.ts';
import { useAccountantInfoStore } from '@/store/useAccountantInfoStore.ts';
import { useUserStore } from '@/store/user';

export const useMarketsStore = defineStore('marketsStore', () => {
  const userStore = useUserStore();
  const { isAccountantRole } = storeToRefs(userStore);

  const accountantInfoStore = useAccountantInfoStore();
  const { firmMarkets } = storeToRefs(accountantInfoStore);

  const markets = ref<Array<Market>>([]);
  const simpleFilteredMarkets = ref<Array<SimpleMarket>>([]);

  const defaultSimpleMarket = ref<SimpleMarket>({
    id: 1,
    name: 'Estonia',
  });

  const selectedMarket = useStorage<SimpleMarket>(
    'selected_market',
    defaultSimpleMarket.value,
    undefined,
    { serializer: StorageSerializers.object },
  );

  const displayedMarkets = computed(() => {
    if (isAccountantRole.value && simpleFilteredMarkets.value?.length > 0) {
      return simpleFilteredMarkets.value;
    } else {
      return markets.value;
    }
  });

  const fetchAndStoreMarkets = async (): Promise<void> => {
    try {
      const data = (await apolloService.makeApolloRequest('query', queries.GET_MARKETS,
        undefined,
        undefined,
        true,
        { fetchPolicy: 'cache-and-network' })) as Query;

      const marketsData = data?.accounting?.getMarkets;
      if (!marketsData) {
        console.error('Received data does not contain markets');
        return;
      }
      markets.value = marketsData;
    } catch (error) {
      console.error('Error fetching markets:', error);
      throw error;
    }
  };

  const fetchMarketsAndSetNewMarket = async (): Promise<void> => {
    const validFirmMarkets = Array.isArray(firmMarkets.value) ? firmMarkets.value : [];

    if (markets.value?.length === 0) {
      await fetchAndStoreMarkets();
    }

    const missingMarkets = validFirmMarkets.filter(firmMarketId =>
      !markets.value.some(market => market.id === firmMarketId),
    );

    if (missingMarkets?.length > 0) {
      await fetchAndStoreMarkets();
    }

    if (isAccountantRole.value && firmMarkets.value?.length > 0) {
      updateSimpleFilteredMarkets();

      const isSelectedValid = simpleFilteredMarkets.value.some(m => m.id === selectedMarket.value.id);

      if (!isSelectedValid) {
        updateSelectedMarket(simpleFilteredMarkets.value[0]);
      }
    } else {
      const isSelectedValid = markets.value.some(m => m.id === selectedMarket.value.id);

      if (!isSelectedValid) {
        updateSelectedMarket(defaultSimpleMarket.value);
      }
    }
  };

  const updateSelectedMarket = (market: SimpleMarket): void => {
    if (!market || selectedMarket.value?.id === market.id) return;
    selectedMarket.value = market;
  };

  const updateSimpleFilteredMarkets = (): void => {
    simpleFilteredMarkets.value = isAccountantRole.value && firmMarkets.value?.length > 0
      ? markets.value.reduce((acc, market) => {
        if (firmMarkets.value.includes(market.id)) {
          acc.push({ id: market.id, name: market.name });
        }
        return acc;
      }, [] as SimpleMarket[])
      : [...markets.value];
  };

  const clearMarkets = (): void => {
    markets.value = [];
  };

  return { displayedMarkets, selectedMarket, updateSelectedMarket, fetchMarketsAndSetNewMarket, clearMarkets };
});
